import axiosWithAuth from "../utils/axiosWithAuth";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

// ===== FUNCIONES PARA MANEJO DE IPR REQUIREMENTS =====

/**
 * Obtener todos los IPR Requirements
 * @param {string} clientId - ID del cliente principal
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const getIPRRequirements = async (clientId) => {
  try {
    if (!clientId) {
      throw new Error("clientId es requerido");
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/ipr-requirements`;
    
    console.log(`Obteniendo IPR Requirements para clientId: ${clientId}`);
    
    const response = await axiosWithAuth.get(url);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo IPR Requirements:", error);
    throw error;
  }
};

/**
 * Obtener un IPR Requirement específico por ID
 * @param {string} clientId - ID del cliente principal
 * @param {string} requirementId - ID del requirement
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const getIPRRequirementById = async (clientId, requirementId) => {
  try {
    if (!clientId || !requirementId) {
      throw new Error("clientId y requirementId son requeridos");
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/ipr-requirements/${requirementId}`;
    
    console.log(`Obteniendo IPR Requirement ${requirementId} para clientId: ${clientId}`);
    
    const response = await axiosWithAuth.get(url);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo IPR Requirement por ID:", error);
    throw error;
  }
};

/**
 * Crear uno o múltiples IPR Requirements
 * @param {string} clientId - ID del cliente principal
 * @param {Array|Object} requirementsData - Array de requirements o un requirement individual
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const createIPRRequirements = async (clientId, requirementsData) => {
  try {
    if (!clientId) {
      throw new Error("clientId es requerido");
    }

    if (!requirementsData) {
      throw new Error("Los datos de requirements son requeridos");
    }

    // Si se pasa un objeto individual, convertirlo a array
    let requirements;
    if (Array.isArray(requirementsData)) {
      requirements = requirementsData;
    } else {
      requirements = [requirementsData];
    }

    // Validar que cada requirement tenga al menos el campo name
    for (const requirement of requirements) {
      if (!requirement.name) {
        throw new Error("Cada requirement debe tener un nombre");
      }
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/ipr-requirements`;
    
    const payload = { requirements };
    
    console.log(`Creando IPR Requirements para clientId: ${clientId}`, payload);
    
    const response = await axiosWithAuth.post(url, payload);
    return response.data;
  } catch (error) {
    console.error("Error creando IPR Requirements:", error);
    throw error;
  }
};

/**
 * Actualizar un IPR Requirement existente
 * @param {string} clientId - ID del cliente principal
 * @param {string} requirementId - ID del requirement a actualizar
 * @param {object} requirementData - Datos del requirement a actualizar
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const updateIPRRequirement = async (clientId, requirementId, requirementData) => {
  try {
    if (!clientId || !requirementId) {
      throw new Error("clientId y requirementId son requeridos");
    }

    if (!requirementData || Object.keys(requirementData).length === 0) {
      throw new Error("Se requieren datos para actualizar el requirement");
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/ipr-requirements/${requirementId}`;
    
    console.log(`Actualizando IPR Requirement ${requirementId} para clientId: ${clientId}`, requirementData);
    
    const response = await axiosWithAuth.put(url, requirementData);
    return response.data;
  } catch (error) {
    console.error("Error actualizando IPR Requirement:", error);
    throw error;
  }
};

/**
 * Eliminar un IPR Requirement
 * @param {string} clientId - ID del cliente principal
 * @param {string} requirementId - ID del requirement a eliminar
 * @param {object} additionalData - Datos adicionales como user_name para logs
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export const deleteIPRRequirement = async (clientId, requirementId, additionalData = {}) => {
  try {
    if (!clientId || !requirementId) {
      throw new Error("clientId y requirementId son requeridos");
    }

    const url = `${API_URL}/workflows/p2p/${clientId}/ipr-requirements/${requirementId}`;
    
    console.log(`Eliminando IPR Requirement ${requirementId} para clientId: ${clientId}`);
    
    const response = await axiosWithAuth.delete(url, { data: additionalData });
    return response.data;
  } catch (error) {
    console.error("Error eliminando IPR Requirement:", error);
    throw error;
  }
};

// ===== FUNCIONES AUXILIARES =====

/**
 * Obtener estadísticas de IPR Requirements
 * @param {string} clientId - ID del cliente principal
 * @returns {Promise} - Promesa con estadísticas de requirements
 */
export const getIPRRequirementsStats = async (clientId) => {
  try {
    const response = await getIPRRequirements(clientId);
    
    if (response.success && response.data) {
      const requirements = response.data;
      const stats = {
        total: requirements.length,
        mandatory: requirements.filter(req => req.mandatory).length,
        optional: requirements.filter(req => !req.mandatory).length,
        withDescription: requirements.filter(req => req.description && req.description.trim() !== '').length
      };
      
      return {
        success: true,
        data: stats
      };
    }
    
    return response;
  } catch (error) {
    console.error("Error obteniendo estadísticas de IPR Requirements:", error);
    throw error;
  }
};

/**
 * Buscar IPR Requirements por nombre
 * @param {string} clientId - ID del cliente principal
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Promise} - Promesa con requirements filtrados
 */
export const searchIPRRequirements = async (clientId, searchTerm) => {
  try {
    if (!searchTerm || searchTerm.trim().length < 2) {
      throw new Error("El término de búsqueda debe tener al menos 2 caracteres");
    }

    const response = await getIPRRequirements(clientId);
    
    if (response.success && response.data) {
      const filteredRequirements = response.data.filter(requirement => 
        requirement.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (requirement.description && requirement.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      return {
        success: true,
        data: filteredRequirements,
        total: filteredRequirements.length
      };
    }
    
    return response;
  } catch (error) {
    console.error("Error buscando IPR Requirements:", error);
    throw error;
  }
};

/**
 * Validar datos de IPR Requirement
 * @param {object} requirementData - Datos del requirement a validar
 * @returns {object} - Resultado de la validación
 */
export const validateIPRRequirementData = (requirementData) => {
  const errors = [];
  
  if (!requirementData) {
    errors.push("Los datos del requirement son requeridos");
    return { isValid: false, errors };
  }
  
  if (!requirementData.name || requirementData.name.trim() === '') {
    errors.push("El nombre del requirement es requerido");
  }
  
  if (requirementData.name && requirementData.name.length > 255) {
    errors.push("El nombre del requirement no puede exceder 255 caracteres");
  }
  
  if (requirementData.description && requirementData.description.length > 1000) {
    errors.push("La descripción no puede exceder 1000 caracteres");
  }
  
  if (requirementData.mandatory !== undefined && typeof requirementData.mandatory !== 'boolean') {
    errors.push("El campo mandatory debe ser un valor booleano");
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Crear múltiples IPR Requirements por lotes
 * @param {string} clientId - ID del cliente principal
 * @param {Array} requirementsArray - Array de requirements
 * @param {number} batchSize - Tamaño del lote (por defecto 10)
 * @returns {Promise} - Promesa con el resultado de todas las operaciones
 */
export const createIPRRequirementsBatch = async (clientId, requirementsArray, batchSize = 10) => {
  try {
    if (!Array.isArray(requirementsArray) || requirementsArray.length === 0) {
      throw new Error("Se requiere un array no vacío de requirements");
    }

    const results = [];
    const errors = [];

    // Procesar en lotes
    for (let i = 0; i < requirementsArray.length; i += batchSize) {
      const batch = requirementsArray.slice(i, i + batchSize);
      
      try {
        const result = await createIPRRequirements(clientId, batch);
        results.push(result);
      } catch (error) {
        errors.push({
          batch: Math.floor(i / batchSize) + 1,
          error: error.message,
          items: batch
        });
      }
    }

    return {
      success: errors.length === 0,
      results,
      errors,
      totalProcessed: requirementsArray.length,
      successfulBatches: results.length,
      failedBatches: errors.length
    };
  } catch (error) {
    console.error("Error creando IPR Requirements por lotes:", error);
    throw error;
  }
};

/* 
===== EJEMPLOS DE USO =====

// 1. CREAR MÚLTIPLES REQUIREMENTS (según el formato del endpoint POST)
const requirementsData = [
  {
    "name": "Factura Proforma",
    "description": "Documento requerido para compras internacionales",
    "mandatory": true
  },
  {
    "name": "Cotización",
    "description": "Cotización formal del proveedor", 
    "mandatory": false
  }
];

try {
  const result = await createIPRRequirements("client123", requirementsData);
  console.log("Requirements creados:", result);
  // Respuesta esperada:
  // {
  //   success: true,
  //   data: [...], // Array de requirements creados con sus IDs
  //   message: "Requirements creados exitosamente"
  // }
} catch (error) {
  console.error("Error:", error.message);
}

// 2. CREAR UN REQUIREMENT INDIVIDUAL
const singleRequirement = {
  "name": "Certificado de Origen",
  "description": "Certificado que acredita el origen de los productos",
  "mandatory": true
};

try {
  const result = await createIPRRequirements("client123", singleRequirement);
  console.log("Requirement creado:", result);
} catch (error) {
  console.error("Error:", error.message);
}

// 3. OBTENER TODOS LOS REQUIREMENTS
try {
  const result = await getIPRRequirements("client123");
  console.log("Requirements obtenidos:", result);
  // Respuesta esperada:
  // {
  //   success: true,
  //   data: [
  //     {
  //       id: "req1",
  //       name: "Factura Proforma",
  //       description: "Documento requerido para compras internacionales",
  //       mandatory: true,
  //       created_at: "2024-01-01T00:00:00Z",
  //       updated_at: "2024-01-01T00:00:00Z"
  //     },
  //     ...
  //   ]
  // }
} catch (error) {
  console.error("Error:", error.message);
}

// 4. OBTENER UN REQUIREMENT POR ID
try {
  const result = await getIPRRequirementById("client123", "req1");
  console.log("Requirement específico:", result);
} catch (error) {
  console.error("Error:", error.message);
}

// 5. ACTUALIZAR UN REQUIREMENT
const updateData = {
  name: "Factura Proforma Actualizada",
  description: "Descripción actualizada del documento",
  mandatory: false
};

try {
  const result = await updateIPRRequirement("client123", "req1", updateData);
  console.log("Requirement actualizado:", result);
} catch (error) {
  console.error("Error:", error.message);
}

// 6. ELIMINAR UN REQUIREMENT
try {
  const result = await deleteIPRRequirement("client123", "req1", { user_name: "admin" });
  console.log("Requirement eliminado:", result);
} catch (error) {
  console.error("Error:", error.message);
}

// 7. OBTENER ESTADÍSTICAS
try {
  const stats = await getIPRRequirementsStats("client123");
  console.log("Estadísticas:", stats);
  // Respuesta esperada:
  // {
  //   success: true,
  //   data: {
  //     total: 5,
  //     mandatory: 3,
  //     optional: 2,
  //     withDescription: 4
  //   }
  // }
} catch (error) {
  console.error("Error:", error.message);
}

// 8. BUSCAR REQUIREMENTS
try {
  const searchResults = await searchIPRRequirements("client123", "factura");
  console.log("Resultados de búsqueda:", searchResults);
} catch (error) {
  console.error("Error:", error.message);
}

// 9. VALIDAR DATOS ANTES DE ENVIAR
const dataToValidate = {
  name: "Nuevo Requirement",
  description: "Descripción del requirement",
  mandatory: true
};

const validation = validateIPRRequirementData(dataToValidate);
if (validation.isValid) {
  console.log("Datos válidos, procediendo a crear...");
  // Llamar a createIPRRequirements
} else {
  console.error("Errores de validación:", validation.errors);
}

// 10. CREAR POR LOTES (BULK)
const largeRequirementsArray = [
  { name: "Req 1", description: "Desc 1", mandatory: true },
  { name: "Req 2", description: "Desc 2", mandatory: false },
  // ... más requirements
];

try {
  const batchResult = await createIPRRequirementsBatch("client123", largeRequirementsArray, 5);
  console.log("Resultado del procesamiento por lotes:", batchResult);
  // {
  //   success: true/false,
  //   results: [...], // Resultados exitosos
  //   errors: [...], // Errores por lote
  //   totalProcessed: 10,
  //   successfulBatches: 2,
  //   failedBatches: 0
  // }
} catch (error) {
  console.error("Error en procesamiento por lotes:", error.message);
}

*/
